import React, { useState, useEffect, useCallback, useRef } from 'react';

import api from '../services/api';
import { localDateStr } from '../utils/dateUtils';

import Modal from '../components/common/Modal';

import Pagination from '../components/common/Pagination';

import { generateQuotePDF } from '../utils/pdfGenerator';

import TypeaheadSelect from '../components/common/TypeaheadSelect';

import { useAuth } from '../context/AuthContext';

import { useDebounce } from '../hooks/useDebounce';

import { useCompany } from '../context/CompanyContext';



const STATUSES = ['new', 'quoted', 'negotiating', 'won', 'lost', 'on_hold'];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'JPY', 'AED', 'SGD'];

const STOCK_STATUS_OPTIONS = ['In stock', '5-6 weeks', 'Cannot offer', '2 weeks'];

const statusColors = { new: 'info', quoted: 'primary', negotiating: 'warning', won: 'success', lost: 'error', on_hold: 'neutral' };

const typeLabels = { product: 'Product', cro_service: 'CRO Service', custom_synthesis: 'Custom Synthesis', method_development: 'Method Dev', other: 'Other' };

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const emptyItem = { catalog_item_id: '', item_name: '', pack_size: '', price: '', quantity: 1, discount_percent: 0, gst_rate: 18, stock_status: '' };



const CATALOG_CATEGORIES = ['impurity_standard', 'reference_standard', 'metabolite', 'degradation_product', 'synthesis_intermediate', 'custom'];

const PHARMACOPEIAS = ['USP', 'EP', 'BP', 'JP', 'ICH', 'In-house', 'N/A'];

const catLbl = c => c.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const emptyNewProd = { name: '', category: 'impurity_standard', catalog_number: '', cas_number: '', pharmacopeia: 'In-house', pack_sizes: '', price: '', description: '', hsn_code: '', prasi_lab_catalog: false };



const defaultForm = {

  client_id: '', contact_id: '', assigned_to: '',

  inquiry_date: localDateStr(),

  type: 'product', subject: '', details: '', notes: '',

  currency: 'INR', status: 'new',

  follow_up_date: '', discount_percent: 0,

  quote_expiry_date: '', reference_number: '', payment_terms: '',

  billing_address: '', shipping_address: '',

  items: [{ ...emptyItem }],

  freight_amount: 0,

};



function buildAddr(parts) {

  return parts.filter(p => p && String(p).trim()).map(p => String(p).trim()).join(', ');

}



function autoFillClientAddresses(client) {

  if (!client) return { billing_address: '', shipping_address: '' };

  const billing = buildAddr([

    client.billing_address || client.address,

    client.billing_city || client.city,

    client.billing_state || client.state,

    client.billing_zip_code || client.zip_code,

    client.billing_country || client.country,

  ]);

  const shipping = buildAddr([

    client.shipping_address || client.billing_address || client.address,

    client.shipping_city || client.billing_city || client.city,

    client.shipping_state || client.billing_state || client.state,

    client.shipping_zip_code || client.billing_zip_code || client.zip_code,

    client.shipping_country || client.billing_country || client.country,

  ]);

  return { billing_address: billing, shipping_address: shipping };

}



function fmtSavedAddr(a) {

  return [a.address, a.city, a.state, a.zip_code, a.country].filter(Boolean).join(', ');

}

const ADDR_TYPE_LABEL = { delivery: 'Delivery', branch: 'Branch', warehouse: 'Warehouse', registered: 'Registered', billing: 'Billing', shipping: 'Shipping', other: 'Other' };



function AddressField({ label, value, onChange, savedAddresses, rightAction }) {

  const [open, setOpen] = React.useState(false);

  const ref = React.useRef(null);

  React.useEffect(() => {

    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);

  }, []);

  return (

    <div className="form-group" style={{ flex: 1 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>

        <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>

          {rightAction}

          {savedAddresses.length > 0 && (

            <div ref={ref} style={{ position: 'relative' }}>

              <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem', padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}

                onClick={() => setOpen(o => !o)}>

                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" /></svg>

                {savedAddresses.length} saved

              </button>

              {open && (

                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 280, maxHeight: 240, overflowY: 'auto', padding: 6 }}>

                  {savedAddresses.map(a => (

                    <button key={a.id} type="button"

                      onClick={() => { onChange(fmtSavedAddr(a)); setOpen(false); }}

                      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '7px 10px', borderRadius: 6, marginBottom: 2 }}

                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}

                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>

                        <span style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{a.label}</span>

                        {a.address_type && a.address_type !== 'other' && (

                          <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 8, background: 'var(--primary-lighter)', color: 'var(--primary)', fontWeight: 600 }}>

                            {ADDR_TYPE_LABEL[a.address_type] || a.address_type}

                          </span>

                        )}

                      </div>

                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{fmtSavedAddr(a) || '—'}</div>

                    </button>

                  ))}

                </div>

              )}

            </div>

          )}

        </div>

      </div>

      <textarea className="form-control" rows={3} placeholder="Street, City, State, ZIP, Country"

        value={value} onChange={e => onChange(e.target.value)}

        style={{ resize: 'vertical', minHeight: 72, fontFamily: 'inherit' }} />

    </div>

  );

}



// ── Catalog search combobox ──────────────────────────────────────────────────

function CatalogCombobox({ catalogItems, value, onSelect, canAddNew, onAddNew }) {

  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);

  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef(null);

  const dropRef = useRef(null);



  const selected = catalogItems.find(c => String(c.id) === String(value));



  useEffect(() => {

    if (selected) setQuery(`${selected.name}${selected.catalog_number ? ` [${selected.catalog_number}]` : ''}`);

    else if (!value) setQuery('');

  }, [selected, value]);



  useEffect(() => {

    const handler = e => {

      if (inputRef.current && !inputRef.current.closest('[data-combobox]').contains(e.target) &&

        dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);

    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);

  }, []);



  const openDrop = () => {

    if (inputRef.current) {

      const r = inputRef.current.getBoundingClientRect();

      setDropPos({ top: r.bottom, left: r.left, width: r.width });

    }

    setOpen(true);

  };



  const filtered = query.trim()

    ? catalogItems.filter(c =>

      c.name.toLowerCase().includes(query.toLowerCase()) ||

      (c.catalog_number || '').toLowerCase().includes(query.toLowerCase()) ||

      (c.cas_number || '').toLowerCase().includes(query.toLowerCase())

    ).slice(0, 20)

    : catalogItems.slice(0, 20);



  const handleSelect = item => {

    setQuery(`${item.name}${item.catalog_number ? ` [${item.catalog_number}]` : ''}`);

    setOpen(false);

    onSelect(item.id);

  };



  return (

    <div data-combobox style={{ position: 'relative' }}>

      <div style={{ position: 'relative' }}>

        <input

          ref={inputRef}

          className="form-control"

          style={{ fontSize: '0.82rem' }}

          placeholder="Search product…"

          value={query}

          onChange={e => { setQuery(e.target.value); openDrop(); if (!e.target.value) onSelect(''); }}

          onFocus={openDrop}

        />

        {value && (

          <button type="button" onClick={() => { setQuery(''); setOpen(false); onSelect(''); }}

            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, lineHeight: 1 }}>

            ×

          </button>

        )}

      </div>

      {open && (filtered.length > 0 || canAddNew) && (

        <div ref={dropRef} style={{

          position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999,

          background: '#fff', border: '1px solid var(--border)', borderRadius: 8,

          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto',

        }}>

          {canAddNew && (

            <div onMouseDown={e => { e.preventDefault(); setOpen(false); onAddNew(); }}

              style={{ padding: '7px 12px', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.82rem', borderBottom: '1px solid var(--border)' }}

              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lighter)'}

              onMouseLeave={e => e.currentTarget.style.background = ''}>

              ＋ Add New Product…

            </div>

          )}

          {filtered.map(item => (

            <div key={item.id} onMouseDown={e => { e.preventDefault(); handleSelect(item); }}

              style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}

              onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-lighter)'}

              onMouseLeave={e => e.currentTarget.style.background = ''}>

              <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{item.name}</div>

              {(item.catalog_number || item.cas_number) && (

                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>

                  {[item.catalog_number && `Cat: ${item.catalog_number}`, item.cas_number && `CAS: ${item.cas_number}`].filter(Boolean).join('  ·  ')}

                </div>

              )}

            </div>

          ))}

        </div>

      )}

    </div>

  );

}



export default function Quotes() {

  const { user, isPrivileged } = useAuth();

  const { companyCurrency } = useCompany();

  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('');

  const [page, setPage] = useState(1);

  const [viewInquiry, setViewInquiry] = useState(null);

  const [modal, setModal] = useState({ open: false, mode: 'add', editId: null });

  const [form, setForm] = useState({ ...defaultForm });

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState(null);



  // Reference data

  const [clients, setClients] = useState([]);

  const [savedAddresses, setSavedAddresses] = useState([]);

  const [contacts, setContacts] = useState([]);

  const [catalogItems, setCatalogItems] = useState([]);

  const [newProdModal, setNewProdModal] = useState({ open: false, itemIndex: null });

  const [newProdForm, setNewProdForm] = useState({ ...emptyNewProd });

  const [newProdSaving, setNewProdSaving] = useState(false);

  const [newProdError, setNewProdError] = useState('');

  const canCreateCatalog = true; // Enable for all users



  const debouncedSearch = useDebounce(search, 300);



  const load = useCallback(async () => {

    setLoading(true);

    try {

      const { data: res } = await api.get('/inquiries', {

        params: { has_quote: 'true', page, limit: 15, search: debouncedSearch || undefined, status: statusFilter || undefined },

      });

      setData(res);

    } finally { setLoading(false); }

  }, [page, debouncedSearch, statusFilter]);



  useEffect(() => { load(); }, [load]);



  const loadFormData = async () => {

    const [cl, cat, usr] = await Promise.all([

      api.get('/clients', { params: { limit: 300, approval_status: 'approved' } }),

      api.get('/catalog/active'),

      isPrivileged ? api.get('/users', { params: { limit: 100 } }) : Promise.resolve({ data: { data: [user] } }),

    ]);

    setClients((cl.data.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));

    setCatalogItems(cat.data.data || []);

  };



  const loadContacts = async (clientId) => {

    if (!clientId) { setContacts([]); return; }

    try {

      const { data: res } = await api.get('/contacts', { params: { client_id: clientId, limit: 100 } });

      setContacts(res.data || []);

    } catch { setContacts([]); }

  };



  const loadEntityAddresses = async (clientId) => {

    if (!clientId) { setSavedAddresses([]); return; }

    try {

      const { data } = await api.get('/addresses', { params: { entity_type: 'client', entity_id: clientId } });

      setSavedAddresses(data.data || []);

    } catch { setSavedAddresses([]); }

  };



  const openAdd = async () => {

    await loadFormData();

    setForm({ ...defaultForm, assigned_to: String(user.id) });

    setContacts([]);

    setSavedAddresses([]);

    setError('');

    setModal({ open: true, mode: 'add', editId: null });

  };



  const openEdit = async (inq) => {

    await loadFormData();

    const { data: res } = await api.get(`/inquiries/${inq.id}`);

    const q = res.data;

    await Promise.all([loadContacts(q.client_id), loadEntityAddresses(q.client_id)]);

    setForm({

      client_id: String(q.client_id || ''),

      contact_id: String(q.contact_id || ''),

      assigned_to: String(q.assigned_to || user.id),

      inquiry_date: q.inquiry_date ? q.inquiry_date.split('T')[0] : localDateStr(),

      type: q.type || 'product',

      subject: q.subject || '',

      details: q.details || '',

      notes: q.notes || '',

      currency: q.currency || 'INR',

      status: q.status || 'quoted',

      follow_up_date: q.follow_up_date ? q.follow_up_date.split('T')[0] : '',

      discount_percent: q.discount_percent || 0,

      quote_expiry_date: q.quote_expiry_date ? q.quote_expiry_date.split('T')[0] : '',

      reference_number: q.reference_number || '',

      payment_terms: q.payment_terms || '',

      billing_address: q.billing_address || '',

      shipping_address: q.shipping_address || '',

      freight_amount: q.freight_amount || 0,

      is_active: q.is_active !== undefined ? q.is_active : 1,

      items: Array.isArray(q.items) && q.items.length > 0
        ? q.items.map(it => ({
          catalog_item_id: it.catalog_item_id,
          item_name: it.item_name || '',
          pack_size: it.pack_size || '',
          price: it.price,
          quantity: it.quantity,
          discount_percent: it.discount_percent || 0,
          gst_rate: it.gst_rate || 0,
          stock_status: it.stock_status || '',
        }))
        : [{ ...emptyItem }],

    });

    setError('');

    setModal({ open: true, mode: 'edit', editId: inq.id });

  };



  const openView = async (inq) => {

    const { data: res } = await api.get(`/inquiries/${inq.id}`);

    setViewInquiry(res.data);

  };



  const handleSave = async () => {

    if (!form.client_id) { setError('Please select a client.'); return; }

    if (form.items.some(it => !it.catalog_item_id || !it.price || !it.quantity)) {

      setError('All item fields (product, qty, price) are required.'); return;

    }

    setSaving(true); setError('');

    const payload = {

      client_id: Number(form.client_id),

      contact_id: form.contact_id ? Number(form.contact_id) : undefined,

      assigned_to: form.assigned_to ? Number(form.assigned_to) : undefined,

      inquiry_date: form.inquiry_date,

      type: form.type || 'product',

      subject: form.subject || 'Quotation',

      details: form.details || undefined,

      notes: form.notes || undefined,

      currency: form.currency,

      status: form.status,

      follow_up_date: form.follow_up_date || undefined,

      quote_expiry_date: form.quote_expiry_date || undefined,

      reference_number: form.reference_number || undefined,

      payment_terms: form.payment_terms || undefined,

      discount_percent: Number(form.discount_percent) || 0,

      billing_address: form.billing_address || undefined,

      shipping_address: form.shipping_address || undefined,

      freight_amount: Number(form.freight_amount) || 0,

      items: form.items.map(it => ({

        catalog_item_id: Number(it.catalog_item_id),

        item_name: it.item_name || undefined,

        pack_size: it.pack_size || undefined,

        quantity: Number(it.quantity),

        price: Number(it.price),

        discount_percent: Number(it.discount_percent) || 0,

        gst_rate: Number(it.gst_rate) || 0,

        stock_status: it.stock_status || '',

      })),

    };

    try {

      if (modal.mode === 'add') {

        await api.post('/inquiries', payload);

      } else {

        await api.put(`/inquiries/${modal.editId}`, payload);

      }

      setModal({ open: false, mode: 'add', editId: null });

      load();

    } catch (err) {

      setError(err.response?.data?.message || 'Failed to save quote.');

    } finally { setSaving(false); }

  };



  const handleDelete = async () => {

    try {

      await api.delete(`/inquiries/${deleteConfirm.id}`);

      setDeleteConfirm(null); load();

    } catch (err) {

      alert(err.response?.data?.message || 'Failed to freeze quote.');

    }

  };



  const handleUnfreeze = async (id) => {

    try {

      await api.patch(`/inquiries/${id}/unfreeze`);

      setForm(p => ({ ...p, is_active: 1 }));

      load();

    } catch (err) {

      alert(err.response?.data?.message || 'Failed to unfreeze quote.');

    }

  };



  const handleDownload = async (inq) => {

    try {

      const { data: res } = await api.get(`/inquiries/${inq.id}`);

      await generateQuotePDF(res.data);

    } catch (err) {

      alert(err.response?.data?.message || 'Failed to generate PDF.');

    }

  };



  /* ── Item helpers ───────────────────────────────────────── */

  const setItem = (i, key, value) => {

    setForm(p => {

      const items = [...p.items];

      items[i] = { ...items[i], [key]: value };

      if (key === 'catalog_item_id') {

        const cat = catalogItems.find(c => c.id === Number(value));

        if (cat) { items[i].item_name = cat.name; items[i].price = cat.price || ''; items[i].pack_size = ''; }

      }

      return { ...p, items };

    });

  };



  /* ── New-product-inline handlers ─────────────────────────── */

  const handleItemCatalogChange = (i, id) => {

    if (id === '__new__') {

      setNewProdForm({ ...emptyNewProd });

      setNewProdError('');

      setNewProdModal({ open: true, itemIndex: i });

      return;

    }

    setItem(i, 'catalog_item_id', id);

  };



  const handleSaveNewProduct = async () => {

    if (!newProdForm.name.trim()) { setNewProdError('Product name is required.'); return; }

    if (!newProdForm.category) { setNewProdError('Category is required.'); return; }

    setNewProdSaving(true); setNewProdError('');

    try {

      const { data } = await api.post('/catalog', { ...newProdForm, price: newProdForm.price || null, currency: companyCurrency });

      const created = data.data;

      setCatalogItems(prev => [...prev, created]);

      const i = newProdModal.itemIndex;

      setForm(p => {

        const items = [...p.items];

        items[i] = { ...items[i], catalog_item_id: created.id, item_name: created.name, price: created.price || '', pack_size: '' };

        return { ...p, items };

      });

      setNewProdModal({ open: false, itemIndex: null });

    } catch (err) {

      setNewProdError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || 'Failed to create product.');

    } finally {

      setNewProdSaving(false);

    }

  };



  const calcLine = it => (Number(it.price) || 0) * (Number(it.quantity) || 0) * (1 - (Number(it.discount_percent) || 0) / 100);





  const subtotal = form.items.reduce((s, it) => s + calcLine(it), 0);

  const discAmt = subtotal * (Number(form.discount_percent) || 0) / 100;

  const taxableAmt = subtotal - discAmt;



  // Recalculate GST based on taxable amount if there's a global discount

  const totalGst = form.items.reduce((s, it) => {

    const itemSubtotal = calcLine(it);

    const itemTaxable = itemSubtotal * (1 - (Number(form.discount_percent) || 0) / 100);

    return s + (itemTaxable * (Number(it.gst_rate) || 0) / 100);

  }, 0);



  const freight = Number(form.freight_amount) || 0;

  // Special freight calculation for quotations: (Net amount + freight) + 18% GST on entire sum
  let grandTotal, payableAmt, rounding;
  if (freight > 0) {
    // Apply 18% GST on (Net amount + freight)
    const amountWithFreight = taxableAmt + freight;
    grandTotal = amountWithFreight * 1.18;
    payableAmt = Math.round(grandTotal);
    rounding = payableAmt - grandTotal;
  } else {
    // Normal calculation without freight
    grandTotal = taxableAmt + totalGst;
    payableAmt = Math.round(grandTotal);
    rounding = payableAmt - grandTotal;
  }



  return (

    <div className="page">

      <div className="page-header">

        <div>

          <h1 className="page-title">Quotes</h1>

          <p className="page-subtitle">Create and manage price quotations with line items</p>

        </div>

        <button className="btn btn-primary" onClick={openAdd}>

          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>

          New Quote

        </button>

      </div>



      {/* Filters */}

      <div className="card" style={{ marginBottom: 16 }}>

        <div className="card-body" style={{ padding: '14px 16px' }}>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>

            <div className="search-input-wrap" style={{ flex: '1 1 220px' }}>

              <span className="search-icon">

                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>

              </span>

              <input className="form-control" placeholder="Search quote #, client, subject…" value={search}

                onChange={e => { setSearch(e.target.value); setPage(1); }} />

            </div>

            <select className="form-control form-select" style={{ width: 150 }} value={statusFilter}

              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>

              <option value="">All Status</option>

              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.replace(/_/g, ' ').slice(1)}</option>)}

            </select>

            {(search || statusFilter) && (

              <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatusFilter(''); setPage(1); }}>Clear</button>

            )}

          </div>

        </div>

      </div>



      {/* Table */}

      <div className="card">

        <div className="table-container">

          <table className="table">

            <thead>

              <tr>

                <th>Quote #</th>

                <th>Client</th>

                <th>Subject</th>

                <th>Type</th>

                <th>Items</th>

                <th>Amount</th>

                <th>Status</th>

                <th>Expiry</th>

                <th>Valid Until</th>

                <th>Actions</th>

              </tr>

            </thead>

            <tbody>

              {loading && <tr><td colSpan={10}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}

              {!loading && data.data.length === 0 && <tr><td colSpan={10}><div className="empty-state"><p>No quotes found. Click "New Quote" to create one.</p></div></td></tr>}

              {!loading && data.data.map(inq => (

                <tr key={inq.id}>

                  <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.82rem' }}>{inq.inquiry_number}</td>

                  <td>

                    <div style={{ fontWeight: 600 }}>{inq.client_name}</div>

                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inq.client_type ? inq.client_type.replace(/_/g, ' ') : ''}</div>

                  </td>

                  <td style={{ maxWidth: 200 }}>

                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{inq.subject || '—'}</div>

                  </td>

                  <td>

                    <span style={{ fontSize: '0.78rem', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 20 }}>

                      {typeLabels[inq.type] || inq.type}

                    </span>

                  </td>

                  <td style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.83rem' }}>

                    {Number(inq.item_count) > 0

                      ? <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{inq.item_count}</span>

                      : '—'}

                  </td>

                  <td style={{ fontWeight: 700 }}>

                    {inq.total_with_gst || inq.quote_amount

                      ? <><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{inq.currency || 'USD'} </span>{Number(inq.total_with_gst || inq.quote_amount).toFixed(2)}</>

                      : '—'}

                  </td>

                  <td>

                    <span className={`badge badge-${statusColors[inq.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>

                      {(inq.status || '').replace(/_/g, ' ')}

                    </span>

                    {inq.is_active === 0 && <span className="badge badge-error" style={{ marginLeft: 4 }}>Frozen</span>}

                  </td>

                  <td style={{ fontSize: '0.82rem' }}>

                    {!inq.quote_expiry_date ? (

                      <span style={{ color: 'var(--text-muted)' }}>—</span>

                    ) : (() => {

                      const today = new Date(); today.setHours(0, 0, 0, 0);

                      const exp = new Date(inq.quote_expiry_date);

                      const daysLeft = Math.ceil((exp - today) / 86400000);

                      if (daysLeft < 0) return <span className="badge badge-error">Expired</span>;

                      if (daysLeft <= 3) return <span className="badge badge-warning">Expiring soon</span>;

                      return <span style={{ color: 'var(--success, #16a34a)', fontWeight: 500 }}>{fmtDate(inq.quote_expiry_date)}</span>;

                    })()}

                  </td>

                  <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{fmtDate(inq.follow_up_date)}</td>

                  <td>

                    <div className="table-actions">

                      <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => openView(inq)}>

                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>

                      </button>

                      {!['won', 'lost'].includes(inq.status) && (

                        <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(inq)}>

                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>

                        </button>

                      )}

                      <button className="btn btn-ghost btn-icon" data-tooltip="Download PDF" onClick={() => handleDownload(inq)}>

                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>

                      </button>

                      <button

                        className="btn btn-ghost btn-icon"

                        data-tooltip="Share on WhatsApp"

                        style={{ color: '#25d366' }}

                        onClick={() => {

                          const amt = inq.quote_amount

                            ? `${inq.currency || 'INR'} ${Number(inq.quote_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

                            : 'N/A';

                          const msg = `Dear Customer, please find your quote #${inq.inquiry_number} from PRASI LABS. Subject: ${inq.subject || 'Quote'}. Amount: ${amt}. View/download on request.`;

                          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');

                        }}

                      >

                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>

                      </button>

                      {isPrivileged && inq.is_active !== 0 && (

                        <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(inq)}>

                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>

                        </button>

                      )}

                    </div>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />

      </div>



      {/* ── Create / Edit Quote Modal ─────────────────────────── */}

      <Modal open={modal.open} onClose={() => setModal({ open: false, mode: 'add', editId: null })}

        title={modal.mode === 'edit' ? 'Edit Quote' : 'Create New Quote'} size="xl"

        footer={<>

          <button className="btn btn-secondary" onClick={() => setModal({ open: false, mode: 'add', editId: null })}>Cancel</button>

          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (modal.mode === 'edit' && form.is_active === 0)}>

            {saving ? 'Saving…' : (modal.mode === 'edit' ? 'Save Changes' : 'Create Quote')}

          </button>

        </>}

      >

        {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}



        {modal.mode === 'edit' && form.is_active === 0 && (

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--error-light, #fff0f0)', border: '1px solid var(--error)', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>

            <span style={{ color: 'var(--error)', fontWeight: 600 }}>⛔ This quote is frozen and cannot be edited.</span>

            <button className="btn btn-secondary btn-sm" onClick={() => handleUnfreeze(modal.editId)}>Unfreeze</button>

          </div>

        )}



        <div className="form-row">

          <div className="form-group" style={{ flex: 2 }}>

            <label className="form-label">Client *</label>

            <TypeaheadSelect

              value={form.client_id}

              options={clients.map(c => ({

                value: c.id,

                label: c.name,

                sub: c.country || undefined,

              }))}

              placeholder="Type to search client…"

              onChange={id => {

                const client = clients.find(c => String(c.id) === String(id));

                const addrFill = autoFillClientAddresses(client);

                setForm(p => ({ ...p, client_id: id, contact_id: '', ...addrFill }));

                if (id) { loadContacts(id); loadEntityAddresses(id); }

              }}

            />

          </div>

          <div className="form-group">

            <label className="form-label">Contact</label>

            <select className="form-control form-select" value={form.contact_id}

              onChange={e => setForm(p => ({ ...p, contact_id: e.target.value }))}>

              <option value="">Select contact</option>

              {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.designation ? ` — ${c.designation}` : ''}</option>)}

            </select>

          </div>

        </div>



        {/* ── Billing & Shipping Addresses ── */}

        <div className="form-row" style={{ alignItems: 'flex-start' }}>

          <AddressField label="Billing Address" value={form.billing_address}

            onChange={v => setForm(p => ({ ...p, billing_address: v }))}

            savedAddresses={savedAddresses} />

          <AddressField label="Shipping Address" value={form.shipping_address}

            onChange={v => setForm(p => ({ ...p, shipping_address: v }))}

            savedAddresses={savedAddresses}

            rightAction={

              <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem', padding: '2px 8px' }}

                onClick={() => setForm(p => ({ ...p, shipping_address: p.billing_address }))}>

                Same as billing

              </button>

            }

          />

        </div>





        <div className="form-row">

          <div className="form-group">

            <label className="form-label">Currency</label>

            <select className="form-control form-select" value={form.currency}

              onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>

              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}

            </select>

          </div>

          <div className="form-group">

            <label className="form-label">Status</label>

            <select className="form-control form-select" value={form.status}

              onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>

              {STATUSES.filter(s => !['won', 'lost'].includes(s)).map(s => (

                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.replace(/_/g, ' ').slice(1)}</option>

              ))}

            </select>

          </div>

          <div className="form-group">

            <label className="form-label">Date</label>

            <input className="form-control" type="date" value={form.inquiry_date}

              onChange={e => setForm(p => ({ ...p, inquiry_date: e.target.value }))} />

          </div>

          <div className="form-group">

            <label className="form-label">Valid Until</label>

            <input className="form-control" type="date" value={form.follow_up_date}

              onChange={e => setForm(p => ({ ...p, follow_up_date: e.target.value }))} />

          </div>

          <div className="form-group">

            <label className="form-label">Quote Expiry Date</label>

            <input className="form-control" type="date" value={form.quote_expiry_date}

              onChange={e => setForm(p => ({ ...p, quote_expiry_date: e.target.value }))} />

          </div>

          <div className="form-group">

            <label className="form-label">Reference #</label>

            <input className="form-control" value={form.reference_number}

              onChange={e => setForm(p => ({ ...p, reference_number: e.target.value }))} />

          </div>

          <div className="form-group">

            <label className="form-label">Payment Terms</label>

            <select className="form-control" value={form.payment_terms} 
              onChange={e => setForm(p => ({ ...p, payment_terms: e.target.value }))}>
                <option value="">Select Payment Term</option>
                <option value="Advance">1. Advance</option>
                <option value="30 days">2. 30 days</option>
                <option value="15 days">3. 15 days</option>
                <option value="50% Advance">4. 50% Advance</option>
                <option value="50% Against Delivery">5. 50% Against Delivery</option>
              </select>

          </div>

        </div>



        {/* Line Items */}

        <div style={{ marginBottom: 14 }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

            <label className="form-label" style={{ marginBottom: 0 }}>Quote Items *</label>

            <button className="btn btn-secondary btn-sm"

              onClick={() => setForm(p => ({ ...p, items: [...p.items, { ...emptyItem }] }))}>

              + Add Item

            </button>

          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>

            <table className="table" style={{ marginBottom: 0 }}>

              <thead>

                <tr>

                  <th>Catalog Item</th>

                  <th style={{ width: 110 }}>Pack Size</th>

                  <th style={{ width: 90 }}>Qty</th>

                  <th style={{ width: 110 }}>Unit Price</th>

                  <th style={{ width: 90 }}>Disc%</th>

                  <th style={{ width: 90 }}>GST%</th>

                  <th style={{ width: 110 }}>Stock Status</th>

                  <th style={{ width: 110 }}>Line Total</th>

                  <th style={{ width: 36 }}></th>

                </tr>

              </thead>

              <tbody>

                {form.items.map((it, i) => {

                  const lineTotal = calcLine(it);

                  return (

                    <tr key={i}>

                      <td>

                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>

                          <div style={{ flex: 1 }}>

                            <CatalogCombobox

                              catalogItems={catalogItems}

                              value={it.catalog_item_id}

                              onSelect={id => handleItemCatalogChange(i, id)}

                              canAddNew={canCreateCatalog}

                              onAddNew={() => handleItemCatalogChange(i, '__new__')}

                            />

                          </div>

                          <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '3px 6px', minWidth: 28, fontSize: '0.88rem' }}

                            onClick={() => handleItemCatalogChange(i, '__new__')}

                            disabled={!canCreateCatalog}

                            title="Add New Product">

                            +

                          </button>

                        </div>

                      </td>

                      <td>

                        <input className="form-control" style={{ fontSize: '0.82rem' }} placeholder="e.g. 10mg"

                          value={it.pack_size} onChange={e => setItem(i, 'pack_size', e.target.value)} />

                      </td>

                      <td>

                        <input className="form-control" style={{ fontSize: '0.82rem' }} type="number" min="1"

                          value={it.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />

                      </td>

                      <td>

                        <input className="form-control" style={{ fontSize: '0.82rem' }} type="number" min="0" step="0.01"

                          value={it.price} onChange={e => setItem(i, 'price', e.target.value)} />

                      </td>

                      <td>

                        <input className="form-control" style={{ fontSize: '0.82rem' }} type="number" min="0" max="100"

                          value={it.discount_percent} onChange={e => setItem(i, 'discount_percent', e.target.value)} />

                      </td>

                      <td>

                        <select className="form-control" style={{ fontSize: '0.82rem', padding: '3px 6px' }}

                          value={it.gst_rate} onChange={e => setItem(i, 'gst_rate', e.target.value)}>

                          <option value="0">0%</option>

                          <option value="5">5%</option>

                          <option value="12">12%</option>

                          <option value="18">18%</option>

                          <option value="28">28%</option>

                        </select>

                      </td>

                      <td>

                        <input className="form-control" style={{ fontSize: '0.82rem', padding: '3px 6px' }}

                          type="text"

                          value={it.stock_status || ''} 

                          onChange={e => setItem(i, 'stock_status', e.target.value)}

                          placeholder="Enter stock status"

                        />

                      </td>

                      <td style={{ fontWeight: 600, fontSize: '0.83rem', textAlign: 'right' }}>

                        {lineTotal.toFixed(2)}

                      </td>

                      <td>

                        {form.items.length > 1 && (

                          <button className="btn btn-ghost btn-icon" style={{ color: 'var(--error)' }}

                            onClick={() => setForm(p => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }))}>×</button>

                        )}

                      </td>

                    </tr>

                  );

                })}

              </tbody>

            </table>

          </div>

        </div>



        {/* Totals + Notes */}

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

          <div style={{ flex: 1 }}>

            <div className="form-group">

              <label className="form-label">Details / Description</label>

              <textarea className="form-control" rows={2} value={form.details}

                onChange={e => setForm(p => ({ ...p, details: e.target.value }))} />

            </div>

            <div className="form-group">

              <label className="form-label">Internal Notes</label>

              <textarea className="form-control" rows={2} value={form.notes}

                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />

            </div>

          </div>

          {/* Summary panel */}

          <div style={{ minWidth: 220, background: 'var(--surface-2)', borderRadius: 10, padding: '12px 16px', border: '1px solid var(--border)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.83rem' }}>

              <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>

              <span>{form.currency} {subtotal.toFixed(2)}</span>

            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>

              <span style={{ fontSize: '0.83rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Discount %</span>

              <input className="form-control" type="number" min="0" max="100" value={form.discount_percent}

                onChange={e => setForm(p => ({ ...p, discount_percent: e.target.value }))}

                style={{ width: 60, fontSize: '0.83rem', padding: '3px 6px' }} />

            </div>

            {discAmt > 0 && (

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.83rem', color: 'var(--error)' }}>

                <span>Discount</span>

                <span>−{form.currency} {discAmt.toFixed(2)}</span>

              </div>

            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.83rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Net Amount</span>
              <span>{form.currency} {taxableAmt.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
              <span style={{ fontSize: '0.83rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Freight Amount</span>
              <input className="form-control" type="number" min="0" value={form.freight_amount}
                onChange={e => setForm(p => ({ ...p, freight_amount: e.target.value }))}
                style={{ width: 80, fontSize: '0.83rem', padding: '3px 6px' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.83rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Net Tax</span>
              <span>{form.currency} {totalGst.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>
              <span style={{ color: 'var(--text-muted)' }}>Gross Amount</span>
              <span>{form.currency} {grandTotal.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.83rem', color: 'var(--text-muted)' }}>
              <span>Rounding Off</span>
              <span>{rounding >= 0 ? '+' : ''}{rounding.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <span>Total Payable</span>
              <span style={{ color: 'var(--primary)' }}>{form.currency} {payableAmt.toFixed(2)}</span>
            </div>

          </div>

        </div>

      </Modal>



      {/* ── New Product Modal ────────────────────────────────── */}

      {newProdModal.open && (

        <div className="modal-overlay" onClick={() => setNewProdModal({ open: false, itemIndex: null })}>

          <div className="modal" style={{ width: 560, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

            <div className="modal-header">

              <h3 className="modal-title">Add New Product to Catalog</h3>

              <button className="modal-close" onClick={() => setNewProdModal({ open: false, itemIndex: null })}>×</button>

            </div>

            <div className="modal-body">

              {newProdError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{newProdError}</div>}

              <div className="form-row">

                <div className="form-group" style={{ flex: 1 }}>

                  <label className="form-label">Product Name *</label>

                  <input className="form-control" value={newProdForm.name}

                    onChange={e => setNewProdForm(p => ({ ...p, name: e.target.value }))}

                    placeholder="e.g. Atorvastatin Impurity A" autoFocus />

                </div>

              </div>

              <div className="form-row">

                <div className="form-group">

                  <label className="form-label">Category *</label>

                  <select className="form-control" value={newProdForm.category}

                    onChange={e => setNewProdForm(p => ({ ...p, category: e.target.value }))}>

                    {CATALOG_CATEGORIES.map(c => <option key={c} value={c}>{catLbl(c)}</option>)}

                  </select>

                </div>

                <div className="form-group">

                  <label className="form-label">Pharmacopeia</label>

                  <select className="form-control" value={newProdForm.pharmacopeia}

                    onChange={e => setNewProdForm(p => ({ ...p, pharmacopeia: e.target.value }))}>

                    {PHARMACOPEIAS.map(ph => <option key={ph} value={ph}>{ph}</option>)}

                  </select>

                </div>

              </div>

              <div className="form-row">

                <div className="form-group">

                  <label className="form-label">CAS Number</label>

                  <input className="form-control" value={newProdForm.cas_number}

                    onChange={e => setNewProdForm(p => ({ ...p, cas_number: e.target.value }))}

                    placeholder="e.g. 1234-56-7" />

                </div>

                <div className="form-group">

                  <label className="form-label">HSN Code</label>

                  <input className="form-control" value={newProdForm.hsn_code}

                    onChange={e => setNewProdForm(p => ({ ...p, hsn_code: e.target.value }))}

                    placeholder="e.g. 30049099" />

                </div>

                <div className="form-group">

                  <label className="form-label">Unit Price ({companyCurrency})</label>

                  <input className="form-control" type="number" min="0" step="0.01"

                    value={newProdForm.price}

                    onChange={e => setNewProdForm(p => ({ ...p, price: e.target.value }))}

                    placeholder="0.00" />

                </div>

              </div>

              <div className="form-group">

                <label className="form-label">Pack Sizes <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(comma-separated, e.g. 5mg, 10mg, 25mg)</span></label>

                <input className="form-control" value={newProdForm.pack_sizes}

                  onChange={e => setNewProdForm(p => ({ ...p, pack_sizes: e.target.value }))}

                  placeholder="5mg, 10mg, 25mg" />

              </div>

              <div className="form-group">

                <label className="form-label">Description</label>

                <textarea className="form-control" rows={2} value={newProdForm.description}

                  onChange={e => setNewProdForm(p => ({ ...p, description: e.target.value }))} />

              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontWeight: 500, fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={!!newProdForm.prasi_lab_catalog} onChange={e => setNewProdForm(p => ({ ...p, prasi_lab_catalog: e.target.checked }))} style={{ width: 16, height: 16 }} />
                  PrasiLab Catalog <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--text-muted)' }}>(made by us; uncheck if vendor product)</span>
                </label>
              </div>

              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>

                This product will be added to the catalog and selected on this line item.

              </p>

            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>

              <button className="btn btn-secondary" onClick={() => setNewProdModal({ open: false, itemIndex: null })}>Cancel</button>

              <button className="btn btn-primary" onClick={handleSaveNewProduct} disabled={newProdSaving}>

                {newProdSaving ? 'Saving…' : 'Create & Select'}

              </button>

            </div>

          </div>

        </div>

      )}



      {/* ── View Quote Modal ─────────────────────────────────── */}

      {viewInquiry && (

        <Modal open onClose={() => setViewInquiry(null)}

          title={`Quote — ${viewInquiry.inquiry_number}`} size="lg"

          footer={

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>

              <button className="btn btn-secondary" onClick={() => handleDownload(viewInquiry)}>

                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>

                Download PDF

              </button>

              <button className="btn btn-secondary" onClick={() => setViewInquiry(null)}>Close</button>

            </div>

          }

        >

          {(() => {

            const q = viewInquiry;

            const items = q.items || [];

            const computedSubtotal = items.reduce((s, it) => s + Number(it.total || 0), 0);

            const discAmt = computedSubtotal * Number(q.discount_percent || 0) / 100;

            const computedTotal = computedSubtotal - discAmt;

            const displayTotal = items.length > 0 ? computedTotal : Number(q.quote_amount || 0);

            return (

              <>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>

                  <span style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--primary)' }}>{q.inquiry_number}</span>

                  <span className={`badge badge-${statusColors[q.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{q.status?.replace(/_/g, ' ')}</span>

                  <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 10, background: 'var(--surface-2)', color: 'var(--text-secondary)' }}>{typeLabels[q.type] || q.type}</span>

                </div>

                <div className="detail-grid" style={{ marginBottom: 16 }}>

                  {[

                    ['Client', q.client_name],

                    ['Contact', q.contact_name || '—'],

                    ['Assigned To', q.assigned_to_name || '—'],

                    ['Date', fmtDate(q.inquiry_date || q.created_at)],

                    ['Valid Until', fmtDate(q.follow_up_date)],

                    ['Currency', q.currency || 'USD'],

                  ].map(([label, value]) => (

                    <div key={label} className="detail-item">

                      <span className="detail-label">{label}</span>

                      <span className="detail-value">{value}</span>

                    </div>

                  ))}

                </div>

                {(q.billing_address || q.shipping_address) && (

                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>

                    {q.billing_address && (

                      <div style={{ flex: 1, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>

                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Billing Address</div>

                        <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{q.billing_address}</div>

                      </div>

                    )}

                    {q.shipping_address && (

                      <div style={{ flex: 1, padding: '10px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>

                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Shipping Address</div>

                        <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{q.shipping_address}</div>

                      </div>

                    )}

                  </div>

                )}

                {q.subject && (

                  <div style={{ marginBottom: 10, fontWeight: 600, fontSize: '0.9rem' }}>{q.subject}</div>

                )}

                {q.details && (

                  <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>

                    {q.details}

                  </div>

                )}



                {/* Line items table */}

                {items.length > 0 && (

                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>

                    <table className="table" style={{ marginBottom: 0 }}>

                      <thead><tr><th>Item</th><th>Cat #</th><th>Pack</th><th>Qty</th><th>Price</th><th>Disc%</th><th>Total</th></tr></thead>

                      <tbody>

                        {items.map(it => (

                          <tr key={it.id}>

                            <td style={{ fontWeight: 500 }}>{it.item_name || '—'}</td>

                            <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{it.catalog_number || '—'}</td>

                            <td>{it.pack_size || '---'}</td>

                            <td style={{ textAlign: 'center' }}>{it.quantity}</td>

                            <td style={{ textAlign: 'right' }}>{q.currency} {Number(it.price).toFixed(2)}</td>

                            <td style={{ textAlign: 'center' }}>{it.discount_percent || 0}%</td>

                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{q.currency} {Number(it.total).toFixed(2)}</td>

                          </tr>

                        ))}

                      </tbody>

                    </table>

                  </div>

                )}



                {/* Totals */}

                <div style={{ textAlign: 'right', background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>

                  {items.length > 0 && (

                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 4 }}>

                      Subtotal: {q.currency} {computedSubtotal.toFixed(2)}

                    </div>

                  )}

                  {discAmt > 0 && (

                    <div style={{ fontSize: '0.875rem', color: 'var(--error)', marginBottom: 4 }}>

                      Discount ({q.discount_percent}%): −{q.currency} {discAmt.toFixed(2)}

                    </div>

                  )}

                  <div style={{ fontWeight: 700, fontSize: '1.1rem', borderTop: items.length > 0 ? '1px solid var(--border)' : 'none', paddingTop: items.length > 0 ? 6 : 0 }}>

                    Quote Total: {q.currency} {displayTotal.toFixed(2)}

                  </div>

                </div>



                {q.notes && (

                  <div style={{ marginTop: 10, padding: 10, background: 'var(--surface-2)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>

                    Notes: {q.notes}

                  </div>

                )}

              </>

            );

          })()}

        </Modal>

      )}



      {/* Delete Confirm */}

      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze Quote"

        footer={<><button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleDelete}>Freeze</button></>}

      >

        <p>Freeze quote <strong>{deleteConfirm?.inquiry_number}</strong> for <strong>{deleteConfirm?.client_name}</strong>? The quote will be locked and cannot be edited until unfrozen.</p>

      </Modal>

    </div>

  );

}

